import { getStudySuggest } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
/** Minimum characters before we fire a suggest request. */
const STUDY_SUGGEST_MIN_CHARS = 2;
/** Debounce window for the suggest input, per the feature spec (~250-300ms). */
const STUDY_SUGGEST_DEBOUNCE_MS = 275;
/**
 * Debounced type-ahead suggestions for the Studies worklist search box.
 *
 * Debounces the raw input, gates on a minimum length, and keeps prior results
 * visible while the next request is in flight (so the dropdown doesn't flicker
 * empty between keystrokes).
 */
export function useStudySuggest(rawQuery, limit = 8) {
    const [debouncedQuery, setDebouncedQuery] = useState(rawQuery);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(rawQuery), STUDY_SUGGEST_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [rawQuery]);
    const trimmed = debouncedQuery.trim();
    const enabled = trimmed.length >= STUDY_SUGGEST_MIN_CHARS;
    const query = useQuery({
        queryKey: ['studySuggest', trimmed, limit],
        queryFn: () => getStudySuggest(trimmed, limit),
        enabled,
        staleTime: 30_000,
        placeholderData: (prev) => prev,
    });
    return {
        suggestions: enabled ? (query.data ?? []) : [],
        isLoading: enabled && query.isLoading,
        isFetching: enabled && query.isFetching,
        debouncedQuery: trimmed,
        enabled,
    };
}
